import { useState } from 'react';

import { CfsApi } from '@/api';
import { UnitShiftAssignModalProps } from '@/components/Modals/UnitShiftListModal/components/UnitShiftAssignModal';
import { useCFSContext } from '@/hooks';
import { IUnit } from '@/models';
import { RadioClickData } from '@/models/radio.click.data';
import { ConfirmTypes, useToastStore } from '@/store';

export const useUnitShiftAssignment = (
  showLoader: () => void,
  hideLoader: () => void,
) => {
  const { cfs } = useCFSContext();
  const { updateToast } = useToastStore();

  const [selectedRadioClick, setSelectedRadioClick] =
    useState<RadioClickData | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [assignUnitShiftModal, setAssignUnitShiftModal] = useState<
    Omit<UnitShiftAssignModalProps, 'onClose' | 'showLoader' | 'hideLoader'>
  >({
    open: false,
  });

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: RadioClickData,
  ) => {
    setSelectedRadioClick(item);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setSelectedRadioClick(null);
    setAnchorEl(null);
  };

  const onCloseModal = () => {
    setAssignUnitShiftModal({
      open: false,
    });
  };

  const assignUnitShift = async (
    unitShiftId = '',
    isDispatchedToHQ?: boolean,
    headquarterName?: string | null,
  ) => {
    showLoader();
    try {
      if (cfs?._id) {
        await CfsApi.assignUnitShift(
          cfs._id,
          unitShiftId,
          isDispatchedToHQ,
          headquarterName,
        );

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Unit shift successfully assigned',
        });
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message,
        type: ConfirmTypes.WARNING,
      });
    } finally {
      hideLoader();
    }
  };

  const handleAssignUnitShift = async (
    unitShiftId = '',
    unit?: IUnit,
    isDispatchedToHQ?: boolean,
    headquarterName?: string | null,
  ) => {
    handleClose();

    if (unitShiftId && cfs?._id) {
      assignUnitShift(unitShiftId, isDispatchedToHQ, headquarterName);
    } else {
      if (!isDispatchedToHQ) {
        setAssignUnitShiftModal({
          open: true,
          unit,
          unitShiftId,
          cfsId: cfs?._id,
        });
      } else if (isDispatchedToHQ) {
        setAssignUnitShiftModal({
          open: true,
          unit,
          unitShiftId,
          cfsId: cfs?._id,
          assignToHQ: true,
          headquarterName,
        });
      }
    }
  };

  return {
    selectedRadioClick,
    anchorEl,
    open: Boolean(anchorEl),
    assignUnitShiftModal,
    handleClick,
    handleClose,
    handleAssignUnitShift,
    onCloseModal,
  };
};
